import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ADDRESS_SUB_TYPE_OPTIONS, ADDRESS_TYPE, ADDRESS_TYPE_OPTIONS, AddressSubType, AddressType } from '@core/address';
import { COMMON_VALIDATION, EMAIL, INDIVIDUAL_NAME, NRIC, PersonnelAppointment } from '@core/data-access';
import { get18YearsAgo } from '@core/util';
import { KeyPersonnelRelationshipToApplicants } from '@portal/data-access';
import { PortalFieldConfig, uppercaseParser } from '@portal/form/data-access';
import { BehaviorSubject, distinctUntilChanged, EMPTY, filter, startWith, tap } from 'rxjs';
import { PERSONNEL_OTHER } from '../const/app.const';
import { AddressModelKeys, AuthorizedPersonnelModelKeys, PersonnelAddressModelKeys } from '../const/form-field-key.const';
import { AuthorizedPersonnelOption } from '../type';
import { ApplicationStore } from '../store';
import { constraintAppointmentOptions, constraintRelationshipOptions } from '../util';
import { AddressFormConfigService } from './address-form-config.service';
import { MobileFormConfigService } from './mobile-form-config.service';

@Injectable({ providedIn: 'root' })
export class AuthorizedPersonnelFormConfigService {
  private readonly mobileFormConfigService = inject(MobileFormConfigService);
  private readonly addressFormConfigService = inject(AddressFormConfigService);
  private readonly applicationStore = inject(ApplicationStore);
  readonly portalPersonnelOptions$: BehaviorSubject<AuthorizedPersonnelOption[]> = new BehaviorSubject<AuthorizedPersonnelOption[]>([]);

  initAuthorizedPersonnelFormFields(): PortalFieldConfig[] {
    return [
      {
        key: AuthorizedPersonnelModelKeys.metadata,
        className: 'hidden',
        fieldGroup: [
          {
            key: 'uuid',
            // type: 'input',
            // props: { label: 'metadata.uuid', disabled: true },
          },
        ],
      },
      {
        key: AuthorizedPersonnelModelKeys.method,
        // type: 'input',
        // props: { label: 'Method', disabled: true },
      },
      {
        key: AuthorizedPersonnelModelKeys.nationalityId,
        // type: 'input',
        // props: { label: 'Nationality', disabled: true },
      },
      {
        key: AuthorizedPersonnelModelKeys.self,
        // type: 'input',
        // props: { label: 'Self', displayWith: value => (JSON.stringify(value)), disabled: true },
      },
      {
        key: AuthorizedPersonnelModelKeys.uuid,
        type: 'mat-select',
        resetOnHide: false,
        expressions: { 'props.disabled': field => this.personnelIsExisted(field) },
        hooks: {
          onInit: ({ formControl, form }) => {
            const formControlChanges$ =
              formControl?.valueChanges.pipe(
                startWith(formControl?.value),
                filter(value => value === PERSONNEL_OTHER || typeof value === 'string'),
                distinctUntilChanged(),
              ) || EMPTY;

            return formControlChanges$.pipe(
              tap(value => {
                const personnels = this.applicationStore.appPersonnelOptionsMap$.getValue();
                const option = personnels.get(value);
                if (option?.personnel && form instanceof FormGroup) {
                  const self = form.getRawValue()?.[AuthorizedPersonnelModelKeys.self];
                  if (self) {
                    form.patchValue({ ...self, self: null });
                    return;
                  }
                  form.patchValue(option.personnel);
                }
              }),
            );
          },
        },
        props: {
          label: 'Select Authorised Personnel',
          placeholder: 'Please select',
          options: this.portalPersonnelOptions$,
          required: true,
        },
      },
      {
        key: AuthorizedPersonnelModelKeys.entityState,
        type: 'input',
        className: 'hidden',
        props: { hidden: true },
      },
      {
        key: AuthorizedPersonnelModelKeys.name,
        type: 'input',
        expressions: { hide: field => this.personnelIsExisted(field) },
        resetOnHide: false,
        props: {
          label: 'Name as per NRIC/Passport',
          placeholder: 'Please enter input',
          required: true,
          maxLength: COMMON_VALIDATION.MAX_LENGTH,
          maskConfig: {
            mask: 'a*',
            config: { patterns: { a: { pattern: INDIVIDUAL_NAME.PATTERN } }, dropSpecialCharacters: false },
          },
        },
      },
      {
        key: AuthorizedPersonnelModelKeys.nricNumber,
        type: 'input',
        expressions: { 'props.disabled': field => this.personnelIsExisted(field) },
        resetOnHide: false,
        props: {
          label: 'NRIC/Passport No.',
          placeholder: 'e.g. S1234567D',
          maxLength: NRIC.MAX_LENGTH,
          minLength: NRIC.MIN_LENGTH,
          required: true,
          maskConfig: { mask: 'A*' },
        },
        // validators: { validation: ['nric'] },
        parsers: [uppercaseParser],
      },
      {
        key: AuthorizedPersonnelModelKeys.dateOfBirthOnly,
        type: 'datepicker',
        expressions: { hide: field => this.personnelIsExisted(field) },
        resetOnHide: false,
        validators: { validation: ['dateRange'] },
        props: {
          label: 'Date of birth',
          placeholder: 'Please enter input',
          // minDate: getDate100YearsAgo(),
          // maxDate: getEndOfToday(),
          maxDate: get18YearsAgo(),
        },
      },
      {
        fieldGroup: this.mobileFormConfigService.initMobileFieldConfig({
          label: 'Mobile Number',
          key: {
            areaCode: AuthorizedPersonnelModelKeys.contactAreaCode,
            number: AuthorizedPersonnelModelKeys.contactNumber,
          },
        }),
      },
      {
        key: AuthorizedPersonnelModelKeys.email,
        type: 'input',
        resetOnHide: false,
        props: {
          label: 'Email Address',
          placeholder: 'Please enter input',
          required: true,
          maxLength: EMAIL.MAX_LENGTH,
        },
        validators: {
          validation: [{ name: 'email-custom', options: { keyName: AuthorizedPersonnelModelKeys.email } }],
        },
        // parsers: [lowercaseParser],
      },
      {
        expressions: { hide: field => this.personnelIsExisted(field) },
        resetOnHide: false,
        className: 'md:col-span-2',
        fieldGroup: this.initPersonnelAddressFormFields(),
      },
      {
        key: AuthorizedPersonnelModelKeys.relationshipToApplicant,
        type: 'mat-select',
        resetOnHide: false,
        props: {
          label: 'Relationship to Applicant',
          placeholder: 'Please select',
          multiple: true,
          required: true,
          options: Array.from(KeyPersonnelRelationshipToApplicants),
        },
        hooks: { onInit: field => constraintRelationshipOptions({ field }) },
      },
      {
        key: AuthorizedPersonnelModelKeys.relationshipToApplicantOther,
        type: 'input',
        resetOnHide: false,
        props: {
          label: 'Relationship to Applicant (Others)',
          placeholder: 'Please enter input',
          required: true,
          maxLength: COMMON_VALIDATION.MAX_LENGTH,
        },
        hooks: { onInit: field => constraintRelationshipOptions({ field }) },
        expressions: { hide: ({ model }) => !model?.[AuthorizedPersonnelModelKeys.relationshipToApplicant]?.includes('OTHER') },
      },
      {
        key: AuthorizedPersonnelModelKeys.appointment,
        type: '#appointment',
        props: {
          required: true,
          multiple: true,
        },
        expressions: {
          'props.hint': ({ props }) => {
            return props?.disabled
              ? null
              : '<div class="italic"><strong>Note:</strong> Select “Authorised Signatory” and/or “Guarantor” when applicable</div>';
          },
        },
        hooks: {
          onInit: field => constraintAppointmentOptions({ field }),
        },
      },
      {
        key: AuthorizedPersonnelModelKeys.preferredContactTime,
        type: '#preferredTime',
        expressions: { hide: ({ model }) => !model?.[AuthorizedPersonnelModelKeys.appointment]?.includes(PersonnelAppointment.KEYMAN) },
        props: { label: 'Keyman Preferred Contact Time', multiple: true },
      },
    ];
  }

  initPersonnelAddressFormFields(): PortalFieldConfig[] {
    return [
      {
        key: AuthorizedPersonnelModelKeys.address,
        fieldGroupClassName: 'field-columns',
        fieldGroup: [
          {
            key: PersonnelAddressModelKeys.residential,
            className: 'sm:col-span-2',
            fieldGroupClassName: 'field-columns',
            fieldGroup: this.initAddressFormFields(AddressType.RESIDENTIAL_ADDRESS),
          },
          {
            className: 'col-start-1',
            key: PersonnelAddressModelKeys.differentResidential,
            type: 'checkbox-boolean',
            defaultValue: false,
            props: {
              label: ADDRESS_TYPE.MAILING_ADDRESS,
              labelCheckbox: 'My Mailing Address is different from Residential Address',
            },
            // expressions: { 'props.disabled': 'formState.isMyInfo || formState.disabled' },
          },
          {
            key: PersonnelAddressModelKeys.mailing,
            className: 'sm:col-span-2',
            resetOnHide: true,
            expressions: { hide: model => !model?.parent?.model?.[PersonnelAddressModelKeys.differentResidential] },
            fieldGroupClassName: 'field-columns',
            fieldGroup: this.initAddressFormFields(AddressType.MAILING_ADDRESS),
          },
        ],
      },
    ];
  }

  private initAddressFormFields(address: AddressType): PortalFieldConfig[] {
    return [
      {
        className: 'col-start-1',
        key: AddressModelKeys.id,
        type: 'input',
        resetOnHide: false,
        hide: true,
        props: {
          label: 'ADDRESS_ID',
        },
      },
      {
        className: 'col-start-1',
        key: AddressModelKeys.type,
        type: 'button',
        defaultValue: address,
        resetOnHide: false,
        hide: true,
        props: {
          label: 'ADDRESS_TYPE',
          required: true,
          options: ADDRESS_TYPE_OPTIONS,
        },
      },
      {
        className: 'col-start-1',
        key: AddressModelKeys.subType,
        type: 'button',
        defaultValue: AddressSubType.LOCAL,
        props: {
          label: ADDRESS_TYPE.RESIDENTIAL_ADDRESS,
          options: ADDRESS_SUB_TYPE_OPTIONS,
        },
        expressions: {
          'props.disabled': field => address === AddressType.RESIDENTIAL_ADDRESS && field.options?.formState?.isMyInfo,
        },
      },
      {
        className: 'sm:col-span-2',
        fieldGroupClassName: 'field-columns',
        expressions: { hide: ({ model }) => model?.[AddressModelKeys.subType] !== AddressSubType.LOCAL },
        fieldGroup: this.addressFormConfigService.initLocalAddressFormFields(address === AddressType.RESIDENTIAL_ADDRESS),
      },
      {
        className: 'sm:col-span-2',
        fieldGroupClassName: 'field-columns',
        expressions: { hide: ({ model }) => model?.[AddressModelKeys.subType] !== AddressSubType.FOREIGN },
        fieldGroup: this.addressFormConfigService.initForeignAddressFormFields(address === AddressType.RESIDENTIAL_ADDRESS),
      },
    ];
  }

  private personnelIsExisted({ model }: PortalFieldConfig): boolean {
    return typeof model?.[AuthorizedPersonnelModelKeys.uuid] === 'string' && model?.[AuthorizedPersonnelModelKeys.uuid] !== PERSONNEL_OTHER;
  }

  private disableControl({ model, formControl }: PortalFieldConfig): void {
    const disabled = this.personnelIsExisted({ model });

    if (disabled) {
      formControl?.disable({ onlySelf: true, emitEvent: false });
    } else {
      formControl?.enable({ onlySelf: true, emitEvent: false });
    }
  }
}
